// Actions contract row 8, Accept, Decline, Propose another time (Mike, 2026-09-24, answer 11, "Answer here, reason
// optional (Recommended)"): "Accept sends your acceptance. Decline opens the reply box for a reason, optional, typed or
// 'Cobrain, ...', and sends it with the decline. Propose opens a time picker and sends it. The card then says it reached
// the organiser."
//
// The API already answers the organiser on the press (decide/calendar-actions.ts respond, sendResponse true): the
// reason goes as the decline's comment (`note`) and the proposed start as `until`. What was missing was here: Decline
// went at once with no reason, Propose sent no time (the engine refused), and nothing said the answer had gone.
import { OptionButton, ActionButton, ActionInput, ActionTextarea } from '../parts/index.js';
import { useEffect, useRef, useState } from 'react';
import { instructionOf } from './api.js';
import { dayMonthTime } from '../day.js';

/** What the decide route answers beside the decision for a calendar option (routes/triage-decide.ts). */
export interface InviteOutcome {
  calendar?: { did?: string; sentToAnyone?: boolean; proposedStart?: string } | null;
  needsGrant?: string | null;
  openInOutlook?: string | null;
}

const whenWord = (iso: string): string => dayMonthTime(new Date(iso));

/** The card's line once the answer has gone: it says what went, and to whom. */
export function answeredWord(kind: string, who: string | null, r: InviteOutcome, reason: string | null = null): string {
  if (r.needsGrant || r.calendar?.sentToAnyone !== true) return 'Cobrain cannot answer in your calendar yet, so nothing was sent. Answer it in Outlook.';
  const to = who ? who : 'the organiser';
  if (kind === 'decline') return `Declined${reason ? ', with your reason' : ''}. Your answer went to ${to}.`;
  if (kind === 'propose_time') return `Proposed ${r.calendar.proposedStart ? whenWord(r.calendar.proposedStart) : 'a new time'}. It went to ${to}.`;
  return `Accepted. Your answer went to ${to}.`;
}

export interface DeclineBoxProps {
  going: string;
  busy?: boolean;
  error?: string | null;
  /** Sends the decline with this reason (empty: none). */
  onDecline: (reason: string) => void;
  /** "Cobrain, ...": asks for the reason to be written, and resolves to the text, which he reads before it goes. */
  onWrite: (instruction: string) => Promise<string | null>;
  onCancel: () => void;
}

export function DeclineBox({ going, busy = false, error = null, onDecline, onWrite, onCancel }: DeclineBoxProps) {
  const [text, setText] = useState('');
  const [writing, setWriting] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  const instruction = instructionOf(text);
  const press = async () => {
    if (busy || writing) return;
    if (instruction === null) { onDecline(text.trim()); return; }
    setWriting(true);
    try {
      const written = await onWrite(instruction);
      if (written !== null) setText(written);
    } finally {
      setWriting(false);
    }
  };

  return (
    <div className="state" data-state="decline">
      <p className="rule">{going}</p>
      <ActionTextarea
        ref={box}
        className="draft"
        aria-label="your reason"
        placeholder="A reason, if you want to give one"
        value={text}
        disabled={busy || writing}
        rows={3}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void press(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
      />
      <div className="teach-block">
        <OptionButton variant="lead" className="go" disabled={busy || writing} onClick={() => void press()}>
          {writing ? 'Writing…' : instruction !== null ? 'Write it' : 'Decline and send'}
        </OptionButton>
        <ActionButton reading type="button" className="link" disabled={busy || writing} onClick={onCancel}>Cancel</ActionButton>
      </div>
      <p className="note">{instruction !== null
        ? 'Nothing is sent. Cobrain writes the reason here and you read it first.'
        : 'The reason goes to the organiser with your decline; leave it empty to decline without one. Start with “Cobrain,” and Cobrain writes it for you to read first.'}</p>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}

const pad = (n: number) => String(n).padStart(2, '0');
const localInput = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** The time the picker opens on: the same time a day later when the invitation's start is known, else tomorrow at ten. */
export function proposeFrom(startsAt: string | null, now: Date = new Date()): Date {
  const s = startsAt ? Date.parse(startsAt) : NaN;
  if (Number.isFinite(s) && s > now.getTime()) return new Date(s + 86_400_000);
  const d = new Date(now.getTime() + 86_400_000);
  d.setHours(10, 0, 0, 0);
  return d;
}

export interface ProposeBoxProps {
  going: string;
  startsAt: string | null;
  busy?: boolean;
  error?: string | null;
  onPropose: (at: Date) => void;
  onCancel: () => void;
}

export function ProposeBox({ going, startsAt, busy = false, error = null, onPropose, onCancel }: ProposeBoxProps) {
  const [pick, setPick] = useState(() => localInput(proposeFrom(startsAt)));
  const at = new Date(pick);
  const valid = Number.isFinite(at.getTime()) && at.getTime() > Date.now();
  return (
    <div className="state" data-state="propose">
      <p className="rule">{going}</p>
      <div className="choices">
        <ActionInput type="datetime-local" aria-label="proposed time" value={pick} min={localInput(new Date())} disabled={busy} onChange={(e) => setPick(e.target.value)} />
      </div>
      <div className="teach-block">
        <OptionButton variant="lead" className="go" disabled={busy || !valid} onClick={() => onPropose(at)}>Propose this time</OptionButton>
        <ActionButton reading type="button" className="link" disabled={busy} onClick={onCancel}>Cancel</ActionButton>
      </div>
      <p className="note">It goes to the organiser as a new time for the meeting, the same length as it is now.</p>
      {error ? <p className="error" role="alert">{error}</p> : null}
    </div>
  );
}
